import React, {useState} from 'react';
import {FormattedMessage} from 'react-intl';
import moment from 'moment';
import {useCreatePriceQuoteGIContext} from '../../contexts/CreatePriceQuoteGIContext';
import {StyledFieldInput} from '../../../_common/components/FieldInput';
import {StyledFieldTextareaAutosize} from '../../../_common/components/FieldTextareaAutosize';
import GridEditableRows from '../GridEditableRows';
import ButtonTextSuggestions from '../../../_common/components/ButtonTextSuggestions';
import {getTotalCommonDocsAmounts} from '../../../CustomerDetails/utils/countTotalAmounts';
import {StyledFieldDatepicker} from '../../../_common/components/FieldDatepicker';
import {useTextSuggestionsContext} from '../../../_common/contexts/TextSuggestionsContext';
import ButtonAction from '../../../_common/components/ButtonAction';
import ModalContainer from '../../../_common/components/ModalContainer';
import FieldSelect from '../../../_common/components/FieldSelect';
import {filterOptions} from '../../../_common/utils/options';
import ModalHeader from '../../../_common/components/ModalHeader';
import ModalContent from '../../../_common/components/ModalContent';
import ModalButtonsContainer from '../../../_common/components/ModalButtonsContainer';
import Unit from '../../../_common/components/Unit';
import {divideThousands, floatValue} from '../../../../../utils/format';
import {documentTypes as T} from '../../../CustomerDetails/constants/documentTypes';
import {optionsDiscountType} from '../../../CustomerDetails/constants/options';
import {buttonCancel, buttonPreview, buttonSubmit} from '../../../_common/styles/styles';

const PopupCreatePriceQuoteGI: React.FC = () => {
  const [show, setShow] = useState<boolean>(false);
  const handleShowModal = () => setShow(true);
  const handleCloseModal = () => setShow(false);

  const {handleCreate, formik, handlePreview} = useCreatePriceQuoteGIContext();
  const {values, setFieldValue, handleChange, isValid} = formik;

  const prefix = 'customer-details-popup-create-document-label';

  const textareaProps = {
    onChange: handleChange,
  };
  const inputProps = {
    dir: 'ltr' as const,
    customInputStyles: 'text-left',
    customContainerStyles: 'w-100 mx-2',
  };
  const isDisabled = !isValid;

  const {textSuggestions} = useTextSuggestionsContext();
  const {description, remarks, footer} = textSuggestions;

  const getSuggestions = (array: typeof description | typeof remarks | typeof footer) =>
    Array.isArray(array) ? array.filter(item => item.documentTypeID === T.PRICE_QUOTE) : [];

  return (
    <div
      onKeyDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      onFocus={e => e.stopPropagation()}
      onMouseOver={e => e.stopPropagation()}
      className={'d-flex align-items-center'}
    >
      <ButtonAction
        variant='link'
        showIcon={false}
        label={<FormattedMessage id="price-quote-details-button-label-createGI" defaultMessage="Create using GI"/>}
        onClick={handleShowModal}
      />
      <ModalContainer show={show} onHide={handleCloseModal} size="xxxl">
        <>
          <ModalHeader
            type="complex"
            icon="/media/common/Edit_primary.svg"
            header={<FormattedMessage id="customer-details-documentInfo-label-button-create-10"
                                      defaultMessage="Create price quote"/>}
          />
          <ModalContent isScrollable={true}>
            <Unit
              text={<FormattedMessage id={`${prefix}-list-header`} defaultMessage="List"/>}
              icon="/media/common/List_primary.svg" customClassnames="mb-4"
            >
              <GridEditableRows/>
            </Unit>

            <div className="d-flex justify-content-between mb-4">
              <Unit text={<FormattedMessage id="customer-details-popup-create-document-date" defaultMessage="Date"/>}
                    icon="/media/common/Date_green.svg">
                <StyledFieldDatepicker
                  value={values.createdDate}
                  // @ts-ignore
                  onChange={(date: Date | string) => setFieldValue('createdDate', new Date(date))}
                  label={<FormattedMessage id="customer-details-popup-create-document-date" defaultMessage="Date"/>}
                  minDate={moment().subtract(1, 'months').format('YYYY-MM-DD')}
                  maxDate={moment().add(1, 'years').format('YYYY-MM-DD')}
                  customContainerStyles="w-175px"
                />
              </Unit>
              <Unit text={<FormattedMessage id={`${prefix}-totalDiscount-header`} defaultMessage="Total discount"/>}
                    icon="/media/common/Discount_red.svg" customClassnames="w-100 mx-4">
                <div className="d-flex justify-content-between">
                  <StyledFieldInput
                    fieldId="amount" value={values.amount}
                    label={<FormattedMessage id={`${prefix}-amount`} defaultMessage="Amount"/> as unknown as string}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFieldValue('amount', floatValue(e))} {...inputProps} />
                  <FieldSelect
                    fieldId="type"
                    value={filterOptions(values.type)(optionsDiscountType)}
                    options={optionsDiscountType}
                    label={<FormattedMessage id={`${prefix}-type`} defaultMessage="Type"/>}
                    onChange={(value: number) => setFieldValue('type', value)}
                    {...inputProps}
                  />
                  <StyledFieldInput
                    fieldId="totalDiscount"
                    label={<FormattedMessage id={`${prefix}-totalDiscount`} defaultMessage="Total discount"/> as unknown as string}
                    value={divideThousands(getTotalCommonDocsAmounts(values, false).totalDiscount)}
                    disabled
                    onChange={formik.handleChange}
                    {...inputProps}
                  />
                </div>
              </Unit>
              <Unit
                text={<FormattedMessage id={`${prefix}-total-header`} defaultMessage="Total"/>}
                icon="/media/common/Money_navy.svg" customClassnames="w-100"
              >
                <div className="d-flex justify-content-between">
                  <StyledFieldInput
                    fieldId="totalWithoutVAT"
                    label={<FormattedMessage id={`${prefix}-totalWithoutVAT`} defaultMessage="Total without VAT"/> as unknown as string}
                    value={divideThousands(getTotalCommonDocsAmounts(values, false).totalWithoutVAT)}
                    onChange={formik.handleChange}
                    disabled
                    {...inputProps}
                  />
                  <StyledFieldInput
                    fieldId="totalVAT"
                    label={<FormattedMessage id={`${prefix}-totalVAT`}
                                             defaultMessage="Total VAT"/> as unknown as string}
                    value={divideThousands(getTotalCommonDocsAmounts(values, false).totalVAT)}
                    onChange={formik.handleChange}
                    disabled
                    {...inputProps}
                  />
                  <StyledFieldInput
                    fieldId="total"
                    label={<FormattedMessage id={`${prefix}-total`} defaultMessage="Total"/> as unknown as string}
                    value={divideThousands(getTotalCommonDocsAmounts(values, false).total)}
                    onChange={formik.handleChange}
                    disabled
                    {...inputProps}
                  />
                </div>
              </Unit>
            </div>
            <Unit
              text={<FormattedMessage id={`${prefix}-desc-header`} defaultMessage="Additional info"/>}
              icon="/media/common/Description_orange.svg"
            >
              <>
                <StyledFieldTextareaAutosize
                  fieldId="description"
                  label={
                    <div className="d-flex justify-content-start align-items-center">
                      <FormattedMessage id={`${prefix}-description`} defaultMessage="description"/>
                      <ButtonTextSuggestions
                        fieldName="description"
                        currentFieldValue={values.description}
                        setFieldValue={setFieldValue}
                        textSuggestionsArray={getSuggestions(description) as unknown[] as never[]}
                        disabled={getSuggestions(description).length === 0}
                      />
                    </div> as unknown as string
                  }
                  value={values.description}
                  {...textareaProps}
                />
                <div className="d-flex justify-content-between mt-4">
                  <StyledFieldTextareaAutosize
                    fieldId="remarks"
                    label={
                      <div className="justify-content-start">
                        <FormattedMessage id={`${prefix}-remarks`} defaultMessage="remarks"/>
                        <ButtonTextSuggestions
                          fieldName="remarks"
                          currentFieldValue={values.remarks}
                          setFieldValue={setFieldValue}
                          textSuggestionsArray={getSuggestions(remarks) as unknown[] as never[]}
                          disabled={getSuggestions(remarks).length === 0}
                        />
                      </div> as unknown as string
                    }
                    value={values.remarks}
                    minRows={20}
                    maxRows={30}
                    customContainerStyles="mr-4"
                    {...textareaProps}
                  />
                  <StyledFieldTextareaAutosize
                    fieldId="footer"
                    label={
                      <div className="justify-content-start">
                        <FormattedMessage id={`${prefix}-footer`} defaultMessage="Footer"/>
                        <ButtonTextSuggestions
                          fieldName="footer"
                          currentFieldValue={values.footer}
                          setFieldValue={setFieldValue}
                          textSuggestionsArray={getSuggestions(footer) as unknown[] as never[]}
                          disabled={getSuggestions(footer).length === 0}
                        />
                      </div> as unknown as string
                    }
                    value={values.footer}
                    minRows={20}
                    maxRows={30}
                    {...textareaProps}
                  />
                </div>
              </>
            </Unit>
          </ModalContent>
          <ModalButtonsContainer>
            <ButtonAction
              variant="cancel"
              label={<FormattedMessage id="button-close" defaultMessage="Close"/>}
              onClick={handleCloseModal} customClassnames={buttonCancel}
            />
            <ButtonAction
              variant="success"
              useIcon="/media/common/Eye_white.svg"
              label={<FormattedMessage id="customer-details-documentInfo-label-button-view" defaultMessage="Preview"/>}
              onClick={async () => await handlePreview()} customClassnames={buttonPreview}
              disabled={isDisabled}
            />
            <ButtonAction
              useIcon="/media/common/Check-in-circle_white.svg"
              label={<FormattedMessage id="button-submit" defaultMessage="Submit"/>}
              onClick={async () => {
                await handleCreate();
                handleCloseModal();
              }}
              disabled={isDisabled}
              customClassnames={buttonSubmit}
            />
          </ModalButtonsContainer>
        </>
      </ModalContainer>
    </div>
  );
};

export default PopupCreatePriceQuoteGI;